/* eslint-disable react/no-array-index-key */
import React, {
  useEffect,
  useState,
  memo,
  useCallback,
  useMemo,
  useRef,
  createRef,
} from 'react';

import cx from 'classnames';
// import FontAwesomeIcon from '@fortawesome/react-fontawesome';
// import faCaretDown from '@fortawesome/fontawesome-free-solid/faCaretDown';
// import faCaretUp from '@fortawesome/fontawesome-free-solid/faCaretUp';

import { devWarning } from 'helpers/logger';
import { CaretDownIcon, CarretUp } from 'styles/assets/icons';

import classes from './deltaTable.module.scss';
import { useDeltaTable } from './DeltaTableProvider';
import { SortOrder, TableHeaderProps } from './interface';
import Resizeable from './Resizeable';

const ASCEND = 'ascending';
const DESCEND = 'descending';

const noop = () => {};

const TableHeader = <T,>({
  // columns,
  onSortChange,
  rowClass,
  sortDirections,
  fixedHeader,
  // sortOrder,
  setSortOrder,
  columnResize,
}: TableHeaderProps<T>) => {
  const { sortOrder, columns, data, autoSizeAllColumns } = useDeltaTable();
  const thRefs = useRef<any>([]);

  const [activeSortKey, setActiveSortKey] = useState<string | undefined>(undefined);
  const SORT_ORDER: SortOrder[] = useMemo(
    () => sortDirections || [ASCEND, DESCEND],
    [sortDirections]
  );

  const handleSortDirection = useCallback(
    (dataIndex: string | undefined) => {
      if (setSortOrder) {
        if (activeSortKey !== dataIndex) {
          setSortOrder(SORT_ORDER[0]);
          setActiveSortKey(dataIndex);
        } else {
          setSortOrder(prev => {
            const value = SORT_ORDER[SORT_ORDER.indexOf(prev) + 1];
            setActiveSortKey(!value ? undefined : dataIndex);
            return sortDirections && !prev ? SORT_ORDER[0] : value;
          });
        }
      }
    },
    [activeSortKey, SORT_ORDER, setSortOrder, sortDirections]
  );

  useEffect(() => {
    const sortData = {
      sortOrder,
      activeSortKey,
    };
    if (typeof onSortChange === 'function') {
      onSortChange(sortData);
    }
  }, [sortOrder, activeSortKey, onSortChange]);

  thRefs.current = columns.map((element, i) => thRefs.current[i] ?? createRef());

  return (
    (<thead data-palette="TableHeader">
      <tr className={cx({ [`${rowClass}`]: rowClass })}>
        {columns.map((column, index) => {
          const { align, fixed, sortable, titleWithAccessor, title, testId } = column;

          if ((titleWithAccessor && title) || (!titleWithAccessor && !title)) {
            devWarning(
              false,
              'Delta Table Header',
              'title or titleWithAccessor required. Avoid using both'
            );
          }

          // causing too many unnecessary consoles
          // if ((titleWithAccessor && !testId) || typeof title !== 'string') {
          //   console.warn('need testId');
          // }

          const cellStyle = {
            ...(column.width && {
              width: column.width,
              minWidth: column.width,
            }),
            ...(column.minWidth && { minWidth: column.minWidth }),
            ...(column.fixed && { zIndex: 10 }),
          };

          if (column.hide) return null;

          const getIndex = column.dataIndex || column.key;
          let resizable = columnResize;
          if ('columnResize' in column) {
            resizable = column.columnResize;
          }
          return (
            (<th
              data-testid={`tableHeader_${testId || title || column.dataIndex}${
                column.sortable
                  ? `_${(column.dataIndex === activeSortKey && sortOrder) || ASCEND}`
                  : ''
              }`}
              onClick={() => (column.sortable ? handleSortDirection(getIndex) : noop)}
              key={`headCell-${index}`}
              ref={thRefs.current[index]}
              style={cellStyle}
              className={cx(classes.headerCell, {
                [`${classes.alignLeft}`]: align === 'left',
                [`${classes.alignCenter}`]: align === 'center',
                [`${classes.alignRight}`]: align === 'right',
                [`${classes.clickable}`]: sortable,
                [`${classes.fixed}`]: fixed,
                [`${classes.fixedHeader}`]: fixedHeader,
                [`${column.headerCellClassName}`]: column.headerCellClassName,
                [`${classes.resizableHover}`]: resizable,
              })}
              data-index={getIndex}
              data-palette="TableHeader">
              {column.sortable ? (
                <div
                  className={cx(classes.sortable, {
                    [`${classes.justifyLeft}`]: align === 'left',
                    [`${classes.justifyCenter}`]: align === 'center',
                    [`${classes.justifyRight}`]: align === 'right',
                  })}
                  data-palette="TableHeader">
                  {titleWithAccessor?.(data as T[]) ?? title}
                  <div className={classes.sortIndicator}>
                    {SORT_ORDER.includes(ASCEND) ? (
                      <span role="img" className={cx(classes.sortIcon)} data-palette="TableHeader">
                        <CarretUp
                          fill={
                            column.dataIndex === activeSortKey && sortOrder === ASCEND
                              ? 'var(--brand-text-primary)'
                              : 'var(--main-text-teritary)'
                          }
                        />
                      </span>
                    ) : null}
                    {SORT_ORDER.includes(DESCEND) ? (
                      <span role="img" className={cx(classes.sortIcon)} data-palette="TableHeader">
                        <CaretDownIcon
                          fill={
                            column.dataIndex === activeSortKey && sortOrder === DESCEND
                              ? 'var(--brand-text-primary)'
                              : 'var(--main-text-teritary)'
                          }
                        />
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : (
                titleWithAccessor?.(data as T[]) ?? title
              )}
              {resizable && (
                <Resizeable
                  thRef={thRefs.current[index].current}
                  autoSizeAllColumns={autoSizeAllColumns}
                  width={
                    (thRefs.current[index]?.current &&
                      parseInt(
                        window.getComputedStyle(thRefs.current[index].current).width,
                        10
                      )) ||
                    Number(column.width)
                  }
                />
              )}
            </th>)
          );
        })}
      </tr>
    </thead>)
  );
};

export default memo(TableHeader);
